import { GetStaticPaths, GetStaticPropsContext } from 'next'
import { Fragment } from 'react'

import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { GetProps } from '~/utils/pageWrapper/types'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import Metatags from '~/components/Metadata/Metatags'
import curriculumSanity from '~/shared/sanity/curriculum'
import {
  selectGradeTitle,
  setCurriculumSubjectContentActionCreator,
  setGradeContentActionCreator,
} from '~/curriculum/slice/curriculum.slice'
import { isParamsCountryCodeAndGradeValid } from '~/curriculum/utils/validateCountryCodeGrade'
import { useAppSelector } from '~/redux/hooks'
import SubjectCurriculumPage from '~/curriculum/pages/SubjectCurriculum.page'
import { useGetCurriculumSubjectContent } from '~/curriculum/hooks/useGetCurriculumSubjectContent'
import { extractCountryCodeAndGradeIdAndSubjectFromQueryParams } from '~/curriculum/utils/extractQueryParams'
import useRedirectV2UserToGradeOverview from '~/curriculum/hooks/useRedirectV2UserToGradeOverview'

export default function SubjectCurriculum(): React.ReactElement {
  // Redirect Curriculum v2 users off this v1 subject page to their v2 grade overview.
  useRedirectV2UserToGradeOverview()

  const title = useAppSelector(selectGradeTitle)
  return (
    <Fragment>
      <Metatags noindex title={title} />
      <SubjectCurriculumPage />
    </Fragment>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  (store) =>
  async ({ params }: GetStaticPropsContext<{ grade: string; countryCode: string; subject: string }>) => {
    const countryCodeWithGradeLabel = params?.countryCode as string
    const grade = params?.grade as string
    if (!isParamsCountryCodeAndGradeValid({ countryCode: countryCodeWithGradeLabel, grade })) {
      return {
        notFound: true,
      }
    }

    const { countryCode, gradeId, subjectOrder } = extractCountryCodeAndGradeIdAndSubjectFromQueryParams({
      countryCode: params?.countryCode as string,
      grade: params?.grade as string,
      subject: params?.subject as string,
    })

    const gradeContent = await curriculumSanity.getCurriculumContentList(gradeId, countryCode)
    const subjectContent = useGetCurriculumSubjectContent(gradeContent, subjectOrder, countryCode)

    store.dispatch(setGradeContentActionCreator(gradeContent))
    store.dispatch(
      setCurriculumSubjectContentActionCreator({
        ...subjectContent,
        subjectUrl: `/curriculum/${countryCodeWithGradeLabel}/${grade}/${subjectOrder}`,
      }),
    )

    return {
      props: {
        path: `/curriculum/${countryCodeWithGradeLabel}/${grade}`,
        namespaces: [...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES, 'curriculum'],
      },
      revalidate: process.env.ENVIRONMENT === 'staging' ? 60 : undefined,
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)
